///
/// EarthBrowser
///
/// Created by Matt Giger on 10/21/2026
/// Copyright (c) 2026 Geodata Labs
///

import { RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { type StormCloudProperties } from "@/earthbrowser/dataset-distribution/clouds/StormCloudsTable"
import { type time, type timedelta } from "@stovokor/math/time"
import { vec4 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * One storm-centered ~2 km infrared cloud frame: a square single-channel
 * cold-index texture (same encoding as the global CloudTextureFrame) that
 * covers the lat/lon box in `bounds`. The box moves with the storm, so a
 * frame pair in flight has two different boxes; the shader maps the globe
 * UV into each frame's own box rather than packing the pair.
 *
 * Clamped at the edges (the shader feathers the box edge out well before
 * the border), mipmapped like the global frame since it is minified from
 * regional range up.
 */
export class StormCloudTextureFrame extends RadarTextureFrame {
	/** Box in global equirectangular UV: minU, minV, maxU, maxV. minU may be
	 *  negative or maxU above 1 when the box crosses the antimeridian; the
	 *  shader wraps the longitude difference */
	public readonly bounds: vec4
	/** Pixels per side */
	public readonly size: number
	public readonly stormId: string

	constructor(context: StovokorContext, row: StormCloudProperties, startTime: time, duration: timedelta) {
		super(context, row.url, startTime, duration, false)
		this.mipmaps = true
		this.stormId = row.storm_id
		this.size = row.width
		this.bounds = new vec4(
			(row.min_lon + 180) / 360,
			(90 - row.max_lat) / 180,
			(row.max_lon + 180) / 360,
			(90 - row.min_lat) / 180,
		)
	}
}
